import BigNumber from "bignumber.js"
import { useEffect, useState, useCallback } from "react"
import TradingChefAbi from "../config/abi/tradingChef.json";
import { Call, multicallv2 } from "../utils/multicall";
import { getTradingChefAddress } from "../utils/addressHelpers";
import { TRADING_FEE_MAX, TRADING_FEE_PERIOD } from "../config/constants";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "state";
import { resetRewardsState, updateData, updateLoading } from "state/rewards/actions";
import { useWeb3React } from "@web3-react/core";

export interface TradingRewardsInfo {
  claimPercent: number;
  rewardsBalance: BigNumber;
  isLoading: boolean;
  fetchData: () => Promise<void>
  resetData: () => void
}

export const useTradingRewardsInfo = (): TradingRewardsInfo => {
  const { account } = useWeb3React()

  const dispatch = useDispatch<AppDispatch>()
  const rewards = useSelector<AppState, AppState['rewards']>((s) => s.rewards)

  const resetData = useCallback(() => {
    dispatch(resetRewardsState());
  }, [resetRewardsState])

  const fetchData = useCallback(async () => {
    if (account) {
      dispatch(updateLoading({ isLoading: true }));
      const calls: Array<Call> = [
        {
          name: 'getUserTotalReward',
          address: getTradingChefAddress(),
          params: [account]
        },
        {
          name: 'lastClaimTime',
          address: getTradingChefAddress(),
          params: [account]
        },
      ];
      const results = await multicallv2(TradingChefAbi, calls, {});

      const rewardTotal = new BigNumber(results[0]?.toString()).dividedBy(1e18);
      const period = Math.floor(new Date().getTime() / 1000) - parseInt(results[1]?.toString());
      const feeReduced = TRADING_FEE_MAX - (TRADING_FEE_MAX * period / TRADING_FEE_PERIOD) > 0 ? TRADING_FEE_MAX - (TRADING_FEE_MAX * period / TRADING_FEE_PERIOD) : 0;
      const feeAmount = rewardTotal.toNumber() * feeReduced / 100;

      dispatch(updateData({
        rewardsBalance: rewardTotal.toString(10),
        claimPercent: rewardTotal.eq(0) ? 0 : (rewardTotal.toNumber() - feeAmount) / rewardTotal.toNumber()
      }))

      dispatch(updateLoading({ isLoading: false }));
    }
  }, [account])

  return {
    ...rewards,
    rewardsBalance: new BigNumber(rewards.rewardsBalance),
    fetchData,
    resetData
  };
}

export const useFetchTradingRewardsInfo = (): TradingRewardsInfo => {
  const { account } = useWeb3React()


  const { claimPercent, rewardsBalance, isLoading, fetchData, resetData } = useTradingRewardsInfo()

  useEffect(() => {
    if (account) {
      fetchData();

    } else {
      resetData();
    }
  }, [account])

  return {
    claimPercent, rewardsBalance, isLoading, fetchData, resetData
  };
}
